import { useState, useEffect, useMemo } from 'react'
import {
  FileText, Download, Calendar, Filter, Clock, CheckCircle,
  TrendingUp, TrendingDown, BarChart3, PieChart, Activity,
  Building2, Scissors, Users, AlertTriangle, X, Eye, FileSpreadsheet,
  Printer, Share2, LineChart as LineChartIcon, BarChart2, Maximize2, RefreshCw,
  Shield
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore, ROLES } from '../store'

// Tipos de reporte - El reporte "Por Hospital" solo está disponible para admins
const allReportTypes = [
  { id: 'cirugias', name: 'Cirugias Realizadas', description: 'Reporte de todas las cirugias por periodo', icon: Scissors, color: 'emerald' },
  { id: 'diferimientos', name: 'Diferimientos', description: 'Analisis de cirugias diferidas y canceladas', icon: AlertTriangle, color: 'amber' },
  { id: 'ocupacion', name: 'Ocupacion de Quirofanos', description: 'Estadisticas de uso de quirofanos', icon: Activity, color: 'blue' },
  { id: 'tiempos', name: 'Tiempos Quirurgicos', description: 'Analisis de duracion de procedimientos', icon: Clock, color: 'purple' },
  { id: 'especialidades', name: 'Por Especialidad', description: 'Desglose de cirugias por especialidad', icon: PieChart, color: 'teal' },
  { id: 'hospitales', name: 'Por Hospital', description: 'Comparativo entre hospitales', icon: Building2, color: 'red', adminOnly: true },
]

// Historial de reportes generados (demo)
const DEMO_HISTORY = [
  { id: 1, tipo: 'cirugias', nombre: 'Cirugias Realizadas', fecha: '2024-01-15', periodo: 'Dic 2023', formato: 'PDF', tamanio: '2.4 MB' },
  { id: 2, tipo: 'diferimientos', nombre: 'Diferimientos', fecha: '2024-01-10', periodo: 'Nov-Dic 2023', formato: 'Excel', tamanio: '1.8 MB' },
  { id: 3, tipo: 'ocupacion', nombre: 'Ocupacion de Quirofanos', fecha: '2024-01-05', periodo: 'Q4 2023', formato: 'PDF', tamanio: '3.1 MB' },
  { id: 4, tipo: 'hospitales', nombre: 'Comparativo Hospitales', fecha: '2024-01-02', periodo: 'Anual 2023', formato: 'Excel', tamanio: '4.5 MB' },
]

// Contador animado
const AnimatedCounter = ({ value, duration = 1000, showDashOnZero = false }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const end = parseInt(value) || 0
    if (end === 0) {
      setCount(0)
      return
    }

    const startTime = Date.now()
    const startValue = 0

    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(startValue + (end - startValue) * easeOut)

      setCount(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [value, duration])

  if (showDashOnZero && (!value || value === 0)) {
    return <span>--</span>
  }

  return <span>{count}</span>
}

// KPI Card
const KPICard = ({ title, value, subtitle, icon: IconComponent, trend, trendValue, color, onClick, isActive, showDashOnZero = false, suffix = '' }) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-emerald-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
    teal: 'from-teal-500 to-teal-600',
  }

  const showDash = showDashOnZero && (!value || value === 0)

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color]} p-5 text-white shadow-lg transition-all duration-300 ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-xl' : ''} ${isActive ? 'ring-4 ring-white/50 scale-[1.02]' : ''}`}
    >
      {isActive && (
        <div className="absolute top-2 right-2">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <div className="text-4xl font-bold mb-1 tracking-tight">
            {showDash ? '--' : <><AnimatedCounter value={value} />{suffix}</>}
          </div>
          <p className="text-white/70 text-xs">{subtitle}</p>
        </div>
        <div className="p-3 bg-white/20 rounded-xl">
          <IconComponent className="w-6 h-6" />
        </div>
      </div>
      {trend && !showDash && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          {trend === 'up' ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          <span className="text-white/90">{trendValue} vs mes anterior</span>
        </div>
      )}
    </div>
  )
}

// Componente de grafico de barras MEJORADO
const BarChart = ({ data, color = '#10b981', showAverage = true, title = '' }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [animated, setAnimated] = useState(false)
  const maxValue = Math.max(...data.map(d => d.value))
  const average = data.reduce((sum, d) => sum + d.value, 0) / data.length

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Colores graduados segun valor
  const getBarColor = (value, baseColor) => {
    const ratio = value / maxValue
    if (ratio >= 0.8) return '#10b981' // Verde alto
    if (ratio >= 0.6) return '#3b82f6' // Azul medio-alto
    if (ratio >= 0.4) return '#f59e0b' // Amarillo medio
    return '#ef4444' // Rojo bajo
  }

  return (
    <div className="relative">
      {/* Grafico */}
      <div className="flex items-end justify-between h-64 gap-3 px-4 pt-8 pb-2">
        {/* Linea de promedio */}
        {showAverage && (
          <div
            className="absolute left-4 right-4 border-t-2 border-dashed border-orange-400 z-10"
            style={{ bottom: `${(average / maxValue) * 200 + 40}px` }}
          >
            <span className="absolute -top-5 right-0 text-xs text-orange-500 font-medium bg-white px-1">
              Prom: {average.toFixed(1)}
            </span>
          </div>
        )}

        {data.map((item, index) => {
          const isHovered = hoveredIndex === index
          const barHeight = animated ? (item.value / maxValue) * 100 : 0

          return (
            <div
              key={index}
              className="flex flex-col items-center flex-1 relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap z-20 shadow-lg">
                  <div className="font-semibold">{item.label}</div>
                  <div className="text-emerald-300">{item.value} cirugias</div>
                  <div className="text-gray-300 text-xs">{((item.value / data.reduce((s,d) => s + d.value, 0)) * 100).toFixed(1)}% del total</div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                    <div className="border-8 border-transparent border-t-gray-800" />
                  </div>
                </div>
              )}

              {/* Valor encima de la barra */}
              <span className={`text-sm font-bold mb-1 transition-all duration-300 ${isHovered ? 'text-emerald-600 scale-110' : 'text-gray-600'}`}>
                {item.value}
              </span>

              {/* Barra */}
              <div
                className={`w-full rounded-t-xl transition-all duration-700 ease-out cursor-pointer ${isHovered ? 'shadow-lg scale-105' : ''}`}
                style={{
                  height: `${barHeight}%`,
                  backgroundColor: isHovered ? '#059669' : getBarColor(item.value, color),
                  minHeight: '4px',
                  transitionDelay: `${index * 50}ms`
                }}
              />

              {/* Label */}
              <span className={`text-xs mt-2 font-medium transition-colors ${isHovered ? 'text-emerald-600' : 'text-gray-500'}`}>
                {item.label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Leyenda de colores */}
      <div className="flex justify-center gap-4 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span className="text-gray-500">Alto (&gt;80%)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500" />
          <span className="text-gray-500">Medio (60-80%)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-500" />
          <span className="text-gray-500">Bajo (40-60%)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span className="text-gray-500">Critico (&lt;40%)</span>
        </div>
      </div>
    </div>
  )
}

// Componente de grafico circular (pie/donut) MEJORADO
const DonutChart = ({ data, title = '' }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [animated, setAnimated] = useState(false)
  const total = data.reduce((sum, d) => sum + d.value, 0)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Crear arcos con animacion
  const createArcPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const start = (startAngle - 90) * Math.PI / 180
    const end = (endAngle - 90) * Math.PI / 180
    const largeArc = endAngle - startAngle > 180 ? 1 : 0

    const x1Outer = 50 + outerRadius * Math.cos(start)
    const y1Outer = 50 + outerRadius * Math.sin(start)
    const x2Outer = 50 + outerRadius * Math.cos(end)
    const y2Outer = 50 + outerRadius * Math.sin(end)

    const x1Inner = 50 + innerRadius * Math.cos(end)
    const y1Inner = 50 + innerRadius * Math.sin(end)
    const x2Inner = 50 + innerRadius * Math.cos(start)
    const y2Inner = 50 + innerRadius * Math.sin(start)

    return `M ${x1Outer} ${y1Outer} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2Outer} ${y2Outer} L ${x1Inner} ${y1Inner} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x2Inner} ${y2Inner} Z`
  }

  let currentAngle = 0
  const segments = data.map((item, index) => {
    const angle = animated ? (item.value / total) * 360 : 0
    const segment = {
      ...item,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
      percentage: ((item.value / total) * 100).toFixed(1)
    }
    currentAngle += angle
    return segment
  })

  const hoveredItem = hoveredIndex !== null ? segments[hoveredIndex] : null

  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-8">
      {/* Grafico */}
      <div className="relative">
        <svg viewBox="0 0 100 100" className="w-56 h-56">
          {/* Sombra */}
          <circle cx="50" cy="52" r="42" fill="rgba(0,0,0,0.1)" />

          {/* Segmentos */}
          {segments.map((item, index) => {
            const isHovered = hoveredIndex === index
            const innerR = isHovered ? 22 : 25
            const outerR = isHovered ? 45 : 42

            return (
              <path
                key={index}
                d={createArcPath(item.startAngle, item.endAngle, innerR, outerR)}
                fill={item.color}
                className="transition-all duration-300 cursor-pointer"
                style={{
                  filter: isHovered ? 'brightness(1.1) drop-shadow(0 4px 6px rgba(0,0,0,0.3))' : 'none',
                  transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                  transformOrigin: '50px 50px'
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            )
          })}

          {/* Centro con total */}
          <circle cx="50" cy="50" r="20" fill="white" className="drop-shadow-sm" />
          <text x="50" y="46" textAnchor="middle" className="text-xs fill-gray-400 font-medium">Total</text>
          <text x="50" y="56" textAnchor="middle" className="text-lg fill-gray-800 font-bold">{total}</text>
        </svg>

        {/* Tooltip flotante */}
        {hoveredItem && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="bg-gray-800 text-white px-4 py-3 rounded-xl shadow-xl text-center min-w-[120px] -mt-32">
              <div className="font-semibold text-sm">{hoveredItem.label}</div>
              <div className="text-2xl font-bold" style={{ color: hoveredItem.color }}>{hoveredItem.value}</div>
              <div className="text-gray-300 text-sm">{hoveredItem.percentage}%</div>
            </div>
          </div>
        )}
      </div>

      {/* Leyenda mejorada */}
      <div className="space-y-3">
        {segments.map((item, index) => {
          const isHovered = hoveredIndex === index
          return (
            <div
              key={index}
              className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-200 cursor-pointer ${isHovered ? 'bg-gray-100 scale-105' : 'hover:bg-gray-50'}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div
                className="w-4 h-4 rounded-full shadow-sm transition-transform"
                style={{ backgroundColor: item.color, transform: isHovered ? 'scale(1.2)' : 'scale(1)' }}
              />
              <div className="flex-1">
                <span className={`font-medium transition-colors ${isHovered ? 'text-gray-900' : 'text-gray-600'}`}>
                  {item.label}
                </span>
              </div>
              <div className="text-right">
                <span className="font-bold text-gray-800">{item.value}</span>
                <span className={`ml-2 text-sm ${isHovered ? 'text-emerald-600 font-semibold' : 'text-gray-400'}`}>
                  {item.percentage}%
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Componente de grafico de linea MEJORADO
const LineChart = ({ data, color = '#3b82f6', showArea = true, showGrid = true }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [animated, setAnimated] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const maxValue = Math.max(...data.map(d => d.value))
  const minValue = Math.min(...data.map(d => d.value))
  const range = maxValue - minValue || 1

  const maxIndex = data.findIndex(d => d.value === maxValue)
  const minIndex = data.findIndex(d => d.value === minValue)

  const getPoint = (d, i) => {
    const x = (i / (data.length - 1)) * 380 + 30
    const y = animated ? 180 - ((d.value - minValue) / range) * 140 - 20 : 180
    return { x, y }
  }

  const points = data.map((d, i) => {
    const { x, y } = getPoint(d, i)
    return `${x},${y}`
  }).join(' ')

  const areaPoints = `30,180 ${points} ${30 + 380},180`

  // Gradiente ID unico
  const gradientId = `lineGradient-${color.replace('#', '')}`

  return (
    <div className="relative">
      <svg viewBox="0 0 420 220" className="w-full h-64">
        <defs>
          {/* Gradiente para el area */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid horizontal */}
        {showGrid && [0, 1, 2, 3, 4, 5].map(i => (
          <g key={i}>
            <line
              x1="30"
              y1={20 + i * 32}
              x2="410"
              y2={20 + i * 32}
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray={i === 0 || i === 5 ? "0" : "4"}
            />
            <text x="10" y={24 + i * 32} className="text-xs fill-gray-400">
              {Math.round(maxValue - (i * range / 5))}%
            </text>
          </g>
        ))}

        {/* Area con gradiente */}
        {showArea && (
          <polygon
            points={areaPoints}
            fill={`url(#${gradientId})`}
            className="transition-all duration-1000"
          />
        )}

        {/* Linea principal */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-1000"
          style={{
            strokeDasharray: animated ? 'none' : '1000',
            strokeDashoffset: animated ? '0' : '1000'
          }}
        />

        {/* Puntos interactivos */}
        {data.map((d, i) => {
          const { x, y } = getPoint(d, i)
          const isHovered = hoveredIndex === i
          const isMax = i === maxIndex
          const isMin = i === minIndex

          return (
            <g key={i}>
              {/* Area de hover invisible */}
              <circle
                cx={x}
                cy={y}
                r="20"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />

              {/* Linea vertical al hacer hover */}
              {isHovered && (
                <line
                  x1={x}
                  y1={20}
                  x2={x}
                  y2={180}
                  stroke={color}
                  strokeWidth="1"
                  strokeDasharray="4"
                  opacity="0.5"
                />
              )}

              {/* Punto */}
              <circle
                cx={x}
                cy={y}
                r={isHovered ? 8 : isMax || isMin ? 6 : 5}
                fill={isMax ? '#10b981' : isMin ? '#ef4444' : color}
                stroke="white"
                strokeWidth="2"
                className="transition-all duration-200"
                style={{
                  filter: isHovered ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none'
                }}
              />

              {/* Labels de max/min */}
              {(isMax || isMin) && !isHovered && (
                <g>
                  <rect
                    x={x - 20}
                    y={isMax ? y - 28 : y + 10}
                    width="40"
                    height="18"
                    rx="4"
                    fill={isMax ? '#10b981' : '#ef4444'}
                  />
                  <text
                    x={x}
                    y={isMax ? y - 15 : y + 23}
                    textAnchor="middle"
                    className="text-xs fill-white font-semibold"
                  >
                    {isMax ? 'MAX' : 'MIN'}
                  </text>
                </g>
              )}

              {/* Tooltip detallado */}
              {isHovered && (
                <g>
                  <rect
                    x={x - 50}
                    y={y - 55}
                    width="100"
                    height="45"
                    rx="8"
                    fill="#1f2937"
                    className="drop-shadow-lg"
                  />
                  <polygon
                    points={`${x - 6},${y - 10} ${x + 6},${y - 10} ${x},${y - 4}`}
                    fill="#1f2937"
                  />
                  <text x={x} y={y - 38} textAnchor="middle" className="text-xs fill-gray-300 font-medium">
                    {d.label}
                  </text>
                  <text x={x} y={y - 20} textAnchor="middle" className="text-sm fill-white font-bold">
                    {d.value}%
                  </text>
                </g>
              )}
            </g>
          )
        })}
      </svg>

      {/* Labels del eje X */}
      <div className="flex justify-between px-8 -mt-2">
        {data.map((d, i) => (
          <span
            key={i}
            className={`text-xs transition-colors ${hoveredIndex === i ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}
          >
            {d.label}
          </span>
        ))}
      </div>

      {/* Leyenda */}
      <div className="flex justify-center gap-6 mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-gray-500">Maximo: {maxValue}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-gray-500">Minimo: {minValue}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-gray-500">Promedio: {(data.reduce((s, d) => s + d.value, 0) / data.length).toFixed(1)}%</span>
        </div>
      </div>
    </div>
  )
}

// Componente de grafico de barras horizontales MEJORADO
const HorizontalBarChart = ({ data, showRanking = true }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [animated, setAnimated] = useState(false)
  const maxValue = Math.max(...data.map(d => d.value))
  const sortedData = [...data].sort((a, b) => b.value - a.value)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Clasificar por rendimiento
  const getPerformanceLevel = (value) => {
    if (value >= 80) return { label: 'Excelente', color: 'bg-emerald-100 text-emerald-700' }
    if (value >= 60) return { label: 'Bueno', color: 'bg-blue-100 text-blue-700' }
    if (value >= 40) return { label: 'Regular', color: 'bg-amber-100 text-amber-700' }
    return { label: 'Bajo', color: 'bg-red-100 text-red-700' }
  }

  return (
    <div className="space-y-4">
      {data.map((item, index) => {
        const isHovered = hoveredIndex === index
        const ranking = sortedData.findIndex(d => d.label === item.label) + 1
        const performance = getPerformanceLevel(item.value)
        const barWidth = animated ? (item.value / maxValue) * 100 : 0

        return (
          <div
            key={index}
            className={`group transition-all duration-300 ${isHovered ? 'scale-[1.02]' : ''}`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="flex items-center gap-4">
              {/* Ranking */}
              {showRanking && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  ranking === 1 ? 'bg-amber-400 text-amber-900' :
                  ranking === 2 ? 'bg-gray-300 text-gray-700' :
                  ranking === 3 ? 'bg-orange-300 text-orange-800' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {ranking}
                </div>
              )}

              {/* Label */}
              <span className={`text-sm font-medium w-24 truncate transition-colors ${isHovered ? 'text-gray-900' : 'text-gray-600'}`}>
                {item.label}
              </span>

              {/* Barra */}
              <div className="flex-1 relative">
                <div className="bg-gray-100 rounded-full h-8 overflow-hidden">
                  <div
                    className="h-full rounded-full flex items-center transition-all duration-700 ease-out relative overflow-hidden"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: item.color,
                      transitionDelay: `${index * 100}ms`
                    }}
                  >
                    {/* Efecto de brillo */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                    {/* Valor dentro de la barra */}
                    <span className={`ml-auto pr-3 text-sm font-bold text-white drop-shadow transition-all ${barWidth > 20 ? 'opacity-100' : 'opacity-0'}`}>
                      {item.value}%
                    </span>
                  </div>
                </div>

                {/* Valor fuera si la barra es pequeña */}
                {barWidth <= 20 && (
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-600 pr-2">
                    {item.value}%
                  </span>
                )}
              </div>

              {/* Badge de rendimiento */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${performance.color} ${isHovered ? 'scale-110' : ''}`}>
                {performance.label}
              </span>
            </div>

            {/* Detalles expandidos al hover */}
            {isHovered && (
              <div className="ml-12 mt-2 p-3 bg-gray-50 rounded-lg text-sm animate-fadeIn">
                <div className="flex gap-6 text-gray-600">
                  <span>Posicion: <strong className="text-gray-800">#{ranking} de {data.length}</strong></span>
                  <span>Diferencia vs mejor: <strong className="text-gray-800">{maxValue - item.value}%</strong></span>
                  <span>Estado: <strong style={{ color: item.color }}>{performance.label}</strong></span>
                </div>
              </div>
            )}
          </div>
        )
      })}

      {/* Resumen */}
      <div className="flex justify-between pt-4 border-t border-gray-100 text-sm">
        <div className="flex gap-4">
          <span className="text-gray-500">Promedio: <strong className="text-gray-700">{(data.reduce((s, d) => s + d.value, 0) / data.length).toFixed(1)}%</strong></span>
          <span className="text-gray-500">Mejor: <strong className="text-emerald-600">{maxValue}%</strong></span>
          <span className="text-gray-500">Menor: <strong className="text-red-600">{Math.min(...data.map(d => d.value))}%</strong></span>
        </div>
      </div>
    </div>
  )
}

// Selector de tipo de grafico
const ChartTypeSelector = ({ currentType, onChange, availableTypes }) => {
  const chartIcons = {
    bar: BarChart2,
    line: LineChartIcon,
    donut: PieChart,
    horizontal: BarChart3
  }

  const chartLabels = {
    bar: 'Barras',
    line: 'Linea',
    donut: 'Circular',
    horizontal: 'Horizontal'
  }

  return (
    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
      {availableTypes.map(type => {
        const Icon = chartIcons[type]
        const isActive = currentType === type
        return (
          <button
            key={type}
            onClick={() => onChange(type)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              isActive
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{chartLabels[type]}</span>
          </button>
        )
      })}
    </div>
  )
}

// Modal de preview del reporte
const ReportPreviewModal = ({ report, dateRange, onClose, onGenerate }) => {
  const [chartType, setChartType] = useState('default')
  const [showOptions, setShowOptions] = useState(false)
  const [chartOptions, setChartOptions] = useState({
    showAverage: true,
    showGrid: true,
    animated: true
  })

  if (!report) return null

  const reportData = allReportTypes.find(r => r.id === report)
  if (!reportData) return null

  const IconComponent = reportData.icon

  // Tipos de grafico disponibles por reporte
  const availableChartTypes = {
    cirugias: ['bar', 'line', 'donut'],
    diferimientos: ['donut', 'bar', 'horizontal'],
    ocupacion: ['line', 'bar'],
    tiempos: ['bar', 'line', 'horizontal'],
    especialidades: ['donut', 'bar', 'horizontal'],
    hospitales: ['horizontal', 'bar', 'donut']
  }

  // Tipo de grafico por defecto
  const defaultChartType = {
    cirugias: 'bar',
    diferimientos: 'donut',
    ocupacion: 'line',
    tiempos: 'bar',
    especialidades: 'donut',
    hospitales: 'horizontal'
  }

  const currentChartType = chartType === 'default' ? defaultChartType[report] : chartType

  // Datos demo para la preview
  const previewData = {
    cirugias: { total: 156, completadas: 142, diferidas: 14, promedioDia: 7.8 },
    diferimientos: { total: 45, porRecursos: 18, porPaciente: 12, porUrgencia: 15 },
    ocupacion: { promedio: 78, pico: 95, valle: 45, horasPico: '09:00 - 14:00' },
    tiempos: { promedio: 2.3, minimo: 0.5, maximo: 8.2, desviacion: 1.1 },
    especialidades: { total: 12, topEsp: 'Ortopedia', topCirugias: 45 },
    hospitales: { total: 6, mejorOcupacion: 'HGZ-68', menorEspera: 'HGR-196' },
  }

  // Datos para graficos
  const chartData = {
    cirugias: [
      { label: 'Lun', value: 24 },
      { label: 'Mar', value: 28 },
      { label: 'Mie', value: 22 },
      { label: 'Jue', value: 31 },
      { label: 'Vie', value: 26 },
      { label: 'Sab', value: 15 },
      { label: 'Dom', value: 10 },
    ],
    diferimientos: [
      { label: 'Por recursos', value: 18, color: '#ef4444' },
      { label: 'Por paciente', value: 12, color: '#3b82f6' },
      { label: 'Por urgencia', value: 15, color: '#8b5cf6' },
    ],
    ocupacion: [
      { label: '6am', value: 45 },
      { label: '8am', value: 65 },
      { label: '10am', value: 88 },
      { label: '12pm', value: 95 },
      { label: '2pm', value: 82 },
      { label: '4pm', value: 70 },
      { label: '6pm', value: 55 },
    ],
    tiempos: [
      { label: '<1h', value: 12 },
      { label: '1-2h', value: 45 },
      { label: '2-3h', value: 38 },
      { label: '3-4h', value: 28 },
      { label: '>4h', value: 15 },
    ],
    especialidades: [
      { label: 'Ortopedia', value: 45, color: '#10b981' },
      { label: 'Cirugia Gral', value: 38, color: '#3b82f6' },
      { label: 'Ginecologia', value: 28, color: '#ec4899' },
      { label: 'Urologia', value: 22, color: '#f59e0b' },
      { label: 'Otros', value: 23, color: '#6b7280' },
    ],
    hospitales: [
      { label: 'HGZ-53', value: 78, color: '#10b981' },
      { label: 'HGZ-57', value: 85, color: '#f59e0b' },
      { label: 'HGZ-68', value: 62, color: '#10b981' },
      { label: 'HGZ-76', value: 91, color: '#ef4444' },
      { label: 'HGR-196', value: 73, color: '#f59e0b' },
      { label: 'HGZ-197', value: 55, color: '#10b981' },
    ],
  }

  const data = previewData[report] || {}

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${reportData.color}-100`}>
              <IconComponent className={`w-5 h-5 text-${reportData.color}-600`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{reportData.name}</h2>
              <p className="text-sm text-gray-500">Vista previa del reporte</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Periodo */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-500 mb-1">Periodo del reporte</p>
            <p className="font-semibold text-gray-800">
              {dateRange.start ? new Date(dateRange.start).toLocaleDateString('es-MX') : '--'}
              {' - '}
              {dateRange.end ? new Date(dateRange.end).toLocaleDateString('es-MX') : '--'}
            </p>
          </div>

          {/* Metricas preview */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Resumen de datos</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {report === 'cirugias' && (
                <>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{data.total}</p>
                    <p className="text-xs text-gray-500">Total cirugias</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{data.completadas}</p>
                    <p className="text-xs text-gray-500">Completadas</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{data.diferidas}</p>
                    <p className="text-xs text-gray-500">Diferidas</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{data.promedioDia}</p>
                    <p className="text-xs text-gray-500">Promedio/dia</p>
                  </div>
                </>
              )}
              {report === 'diferimientos' && (
                <>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{data.total}</p>
                    <p className="text-xs text-gray-500">Total diferidos</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{data.porRecursos}</p>
                    <p className="text-xs text-gray-500">Por recursos</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{data.porPaciente}</p>
                    <p className="text-xs text-gray-500">Por paciente</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{data.porUrgencia}</p>
                    <p className="text-xs text-gray-500">Por urgencia</p>
                  </div>
                </>
              )}
              {report === 'ocupacion' && (
                <>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{data.promedio}%</p>
                    <p className="text-xs text-gray-500">Ocupacion prom.</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{data.pico}%</p>
                    <p className="text-xs text-gray-500">Pico maximo</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{data.valle}%</p>
                    <p className="text-xs text-gray-500">Valle minimo</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-xl font-bold text-purple-600">{data.horasPico}</p>
                    <p className="text-xs text-gray-500">Horas pico</p>
                  </div>
                </>
              )}
              {report === 'tiempos' && (
                <>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{data.promedio}h</p>
                    <p className="text-xs text-gray-500">Tiempo prom.</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{data.minimo}h</p>
                    <p className="text-xs text-gray-500">Minimo</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{data.maximo}h</p>
                    <p className="text-xs text-gray-500">Maximo</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">±{data.desviacion}h</p>
                    <p className="text-xs text-gray-500">Desviacion</p>
                  </div>
                </>
              )}
              {report === 'especialidades' && (
                <>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-teal-600">{data.total}</p>
                    <p className="text-xs text-gray-500">Especialidades</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center col-span-2">
                    <p className="text-xl font-bold text-emerald-600">{data.topEsp}</p>
                    <p className="text-xs text-gray-500">Mayor volumen</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{data.topCirugias}</p>
                    <p className="text-xs text-gray-500">Cirugias top</p>
                  </div>
                </>
              )}
              {report === 'hospitales' && (
                <>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{data.total}</p>
                    <p className="text-xs text-gray-500">Hospitales</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center col-span-2">
                    <p className="text-xl font-bold text-green-600">{data.mejorOcupacion}</p>
                    <p className="text-xs text-gray-500">Mejor ocupacion</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-lg font-bold text-blue-600">{data.menorEspera}</p>
                    <p className="text-xs text-gray-500">Menor espera</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Grafica real con selector */}
          <div className="bg-gray-50 rounded-xl p-6">
            {/* Header del grafico con controles */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h3 className="font-semibold text-gray-800">
                  {report === 'cirugias' && 'Cirugias por dia de la semana'}
                  {report === 'diferimientos' && 'Causas de diferimiento'}
                  {report === 'ocupacion' && 'Ocupacion a lo largo del dia'}
                  {report === 'tiempos' && 'Distribucion de tiempos quirurgicos'}
                  {report === 'especialidades' && 'Cirugias por especialidad'}
                  {report === 'hospitales' && 'Ocupacion por hospital'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">Pasa el cursor sobre el grafico para ver detalles</p>
              </div>

              <div className="flex items-center gap-2">
                {/* Selector de tipo de grafico */}
                <ChartTypeSelector
                  currentType={currentChartType}
                  onChange={setChartType}
                  availableTypes={availableChartTypes[report] || ['bar']}
                />

                {/* Boton de opciones */}
                <button
                  onClick={() => setShowOptions(!showOptions)}
                  className={`p-2 rounded-lg transition-colors ${showOptions ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  title="Opciones de visualizacion"
                >
                  <Filter className="w-4 h-4" />
                </button>

                {/* Boton de reset */}
                <button
                  onClick={() => setChartType('default')}
                  className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                  title="Restablecer vista"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Panel de opciones */}
            {showOptions && (
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 animate-fadeIn">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Opciones de visualizacion</h4>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartOptions.showAverage}
                      onChange={(e) => setChartOptions({ ...chartOptions, showAverage: e.target.checked })}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-600">Mostrar promedio</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chartOptions.showGrid}
                      onChange={(e) => setChartOptions({ ...chartOptions, showGrid: e.target.checked })}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-600">Mostrar grilla</span>
                  </label>
                </div>
              </div>
            )}

            {/* Renderizado dinamico del grafico */}
            <div className="min-h-[300px]">
              {currentChartType === 'bar' && (
                <BarChart
                  data={chartData[report] || chartData.cirugias}
                  color={report === 'tiempos' ? '#8b5cf6' : '#10b981'}
                  showAverage={chartOptions.showAverage}
                />
              )}

              {currentChartType === 'line' && (
                <LineChart
                  data={chartData[report] || chartData.ocupacion}
                  color={report === 'ocupacion' ? '#3b82f6' : '#10b981'}
                  showGrid={chartOptions.showGrid}
                />
              )}

              {currentChartType === 'donut' && (
                <DonutChart
                  data={chartData[report]?.map((d, i) => ({
                    ...d,
                    color: d.color || ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#6b7280'][i % 6]
                  })) || chartData.especialidades}
                />
              )}

              {currentChartType === 'horizontal' && (
                <HorizontalBarChart
                  data={chartData[report]?.map((d, i) => ({
                    ...d,
                    color: d.color || ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#ef4444'][i % 6]
                  })) || chartData.hospitales}
                />
              )}
            </div>
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onGenerate('pdf')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Descargar PDF
            </button>
            <button
              onClick={() => onGenerate('excel')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              <FileSpreadsheet className="w-5 h-5" />
              Descargar Excel
            </button>
            <button
              onClick={() => toast.success('Enviando a impresora...')}
              className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => toast.success('Enlace copiado')}
              className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Share2 className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Reportes() {
  // Auth context
  const user = useAuthStore(state => state.user)
  const isAdmin = user?.rol === ROLES.ADMIN
  const isOperador = user?.rol === ROLES.OPERADOR
  const userHospitalId = user?.hospital_id

  // Filtrar tipos de reporte según rol
  const reportTypes = useMemo(() => {
    return allReportTypes.filter(report => !report.adminOnly || isAdmin)
  }, [isAdmin])

  const [selectedReport, setSelectedReport] = useState(null)
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [loading, setLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [reportHistory, setReportHistory] = useState(DEMO_HISTORY)
  const [filterHistoryType, setFilterHistoryType] = useState('')

  // Stats demo
  const stats = {
    reportesGenerados: 24,
    estesMes: 8,
    descargas: 156,
    tipoMasUsado: 'Cirugias'
  }

  // Filtrar historial
  const filteredHistory = useMemo(() => {
    if (!filterHistoryType) return reportHistory
    return reportHistory.filter(r => r.tipo === filterHistoryType)
  }, [reportHistory, filterHistoryType])

  // Quick date range setters
  const setQuickRange = (type) => {
    const today = new Date()
    const start = new Date()
    const end = new Date()

    switch (type) {
      case 'week':
        start.setDate(today.getDate() - 7)
        break
      case 'month':
        start.setMonth(today.getMonth() - 1)
        break
      case 'quarter':
        start.setMonth(today.getMonth() - 3)
        break
      case 'year':
        start.setFullYear(today.getFullYear() - 1)
        break
    }

    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    })
  }

  const handlePreview = () => {
    if (!selectedReport) {
      toast.error('Seleccione un tipo de reporte')
      return
    }
    if (!dateRange.start || !dateRange.end) {
      toast.error('Seleccione el rango de fechas')
      return
    }
    setShowPreview(true)
  }

  // Generar contenido del reporte
  const generateReportContent = (reportType) => {
    const reportInfo = allReportTypes.find(r => r.id === reportType)
    const periodo = `${new Date(dateRange.start).toLocaleDateString('es-MX')} - ${new Date(dateRange.end).toLocaleDateString('es-MX')}`

    // Datos demo segun el tipo de reporte
    const reportDataMap = {
      cirugias: {
        headers: ['Fecha', 'Paciente', 'NSS', 'Procedimiento', 'Hospital', 'Quirofano', 'Duracion', 'Estado'],
        rows: [
          ['2024-12-15', 'Juan Perez Garcia', '1234567890', 'Apendicectomia', 'HGZ No. 53', 'Q-1', '2.5 hrs', 'Completada'],
          ['2024-12-15', 'Maria Lopez Ruiz', '0987654321', 'Colecistectomia', 'HGZ No. 57', 'Q-2', '1.8 hrs', 'Completada'],
          ['2024-12-16', 'Carlos Sanchez', '1122334455', 'Hernioplastia', 'HGZ No. 68', 'Q-1', '1.2 hrs', 'Completada'],
          ['2024-12-16', 'Ana Martinez', '5544332211', 'Cesarea', 'HGR No. 196', 'Q-3', '1.0 hrs', 'Completada'],
          ['2024-12-17', 'Roberto Diaz', '6677889900', 'Artroscopia', 'HGZ No. 76', 'Q-2', '2.0 hrs', 'Completada'],
        ],
        summary: { total: 156, completadas: 142, diferidas: 14, promedioDia: 7.8 }
      },
      diferimientos: {
        headers: ['Fecha Original', 'Paciente', 'Procedimiento', 'Hospital', 'Motivo', 'Nueva Fecha', 'Estado'],
        rows: [
          ['2024-12-10', 'Pedro Gomez', 'Colecistectomia', 'HGZ No. 53', 'Falta de anestesiologo', '2024-12-18', 'Reprogramada'],
          ['2024-12-11', 'Laura Torres', 'Hernioplastia', 'HGZ No. 57', 'Paciente no se presento', '2024-12-20', 'Reprogramada'],
          ['2024-12-12', 'Miguel Angel', 'Apendicectomia', 'HGZ No. 68', 'Urgencia mayor prioridad', '2024-12-15', 'Completada'],
          ['2024-12-13', 'Sofia Reyes', 'Cesarea', 'HGR No. 196', 'Quirofano en mantenimiento', '2024-12-16', 'Completada'],
        ],
        summary: { total: 45, porRecursos: 18, porPaciente: 12, porUrgencia: 15 }
      },
      ocupacion: {
        headers: ['Hospital', 'Quirofanos', 'Ocupacion Promedio', 'Pico Maximo', 'Horas Pico', 'Cirugias Realizadas'],
        rows: [
          ['HGZ No. 53', '8', '78%', '95%', '09:00-14:00', '245'],
          ['HGZ No. 57', '6', '85%', '98%', '10:00-15:00', '198'],
          ['HGZ No. 68', '10', '62%', '88%', '08:00-13:00', '312'],
          ['HGZ No. 76', '5', '91%', '100%', '09:00-16:00', '156'],
          ['HGR No. 196', '12', '73%', '92%', '07:00-14:00', '380'],
          ['HGZ No. 197', '4', '55%', '78%', '10:00-14:00', '120'],
        ],
        summary: { promedio: 78, pico: 95, valle: 45, horasPico: '09:00-14:00' }
      },
      tiempos: {
        headers: ['Procedimiento', 'Cantidad', 'Tiempo Promedio', 'Tiempo Minimo', 'Tiempo Maximo', 'Desviacion'],
        rows: [
          ['Apendicectomia', '45', '1.5 hrs', '0.8 hrs', '2.5 hrs', '0.4 hrs'],
          ['Colecistectomia', '38', '1.8 hrs', '1.0 hrs', '3.0 hrs', '0.5 hrs'],
          ['Hernioplastia', '52', '1.2 hrs', '0.5 hrs', '2.0 hrs', '0.3 hrs'],
          ['Cesarea', '67', '1.0 hrs', '0.5 hrs', '1.5 hrs', '0.2 hrs'],
          ['Artroscopia', '28', '2.0 hrs', '1.0 hrs', '3.5 hrs', '0.6 hrs'],
        ],
        summary: { promedio: 2.3, minimo: 0.5, maximo: 8.2, desviacion: 1.1 }
      },
      especialidades: {
        headers: ['Especialidad', 'Cirugias Realizadas', 'Porcentaje', 'Tiempo Promedio', 'Diferimientos'],
        rows: [
          ['Ortopedia', '45', '29%', '2.1 hrs', '3'],
          ['Cirugia General', '38', '24%', '1.8 hrs', '5'],
          ['Ginecologia', '28', '18%', '1.2 hrs', '2'],
          ['Urologia', '22', '14%', '1.5 hrs', '4'],
          ['Otros', '23', '15%', '1.6 hrs', '1'],
        ],
        summary: { total: 12, topEsp: 'Ortopedia', topCirugias: 45 }
      },
      hospitales: {
        headers: ['Hospital', 'Quirofanos', 'Ocupacion', 'Cirugias', 'Diferimientos', 'Tiempo Promedio'],
        rows: [
          ['HGZ No. 53', '8', '78%', '245', '12', '1.8 hrs'],
          ['HGZ No. 57', '6', '85%', '198', '15', '1.6 hrs'],
          ['HGZ No. 68', '10', '62%', '312', '8', '1.9 hrs'],
          ['HGZ No. 76', '5', '91%', '156', '18', '1.5 hrs'],
          ['HGR No. 196', '12', '73%', '380', '10', '2.0 hrs'],
          ['HGZ No. 197', '4', '55%', '120', '5', '1.4 hrs'],
        ],
        summary: { total: 6, mejorOcupacion: 'HGZ-68', menorEspera: 'HGR-196' }
      },
    }

    return {
      title: reportInfo?.name || 'Reporte',
      periodo,
      ...reportDataMap[reportType]
    }
  }

  // Descargar como CSV
  const downloadCSV = (reportType) => {
    const content = generateReportContent(reportType)
    const csvRows = [
      [`Reporte: ${content.title}`],
      [`Periodo: ${content.periodo}`],
      [`Fecha de generacion: ${new Date().toLocaleString('es-MX')}`],
      [],
      content.headers,
      ...content.rows,
      [],
      ['RESUMEN'],
      ...Object.entries(content.summary).map(([key, value]) => [key, value])
    ]

    const csvContent = csvRows.map(row => row.join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${reportType}_${dateRange.start}_${dateRange.end}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Descargar como PDF (usando ventana de impresion)
  const downloadPDF = (reportType) => {
    const content = generateReportContent(reportType)
    const printWindow = window.open('', '_blank')

    // Logo Centinela en SVG inline
    const logoSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 80" fill="none" width="180" height="72">
      <g transform="translate(10, 5)">
        <circle cx="35" cy="35" r="32" fill="#006847" stroke="#fff" stroke-width="2"/>
        <path d="M35 12 L45 22 L42 25 L35 20 L28 25 L25 22 Z" fill="#fff"/>
        <path d="M35 20 L40 28 L35 35 L30 28 Z" fill="#fff"/>
        <circle cx="28" cy="42" r="4" fill="#fff"/>
        <rect x="25" y="46" width="6" height="12" rx="2" fill="#fff"/>
        <circle cx="35" cy="40" r="5" fill="#fff"/>
        <rect x="31" y="45" width="8" height="14" rx="2" fill="#fff"/>
        <circle cx="42" cy="42" r="4" fill="#fff"/>
        <rect x="39" y="46" width="6" height="12" rx="2" fill="#fff"/>
        <circle cx="35" cy="52" r="3" fill="#fff"/>
        <rect x="33" y="55" width="4" height="6" rx="1" fill="#fff"/>
      </g>
      <text x="85" y="48" font-family="Arial Black, Arial, sans-serif" font-size="28" font-weight="900" fill="#006847">CENTINELA SALUD</text>
      <text x="85" y="62" font-family="Arial, sans-serif" font-size="8" fill="#006847">Centinela Salud</text>
    </svg>`

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${content.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .logo-header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #006847; }
          .logo-header h1 { color: #006847; margin: 0; font-size: 24px; }
          .logo-header p { color: #666; margin: 5px 0 0 0; font-size: 14px; }
          h2 { color: #006847; margin-top: 30px; }
          .header { margin-bottom: 30px; }
          .periodo { color: #666; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background-color: #006847; color: white; padding: 12px; text-align: left; }
          td { padding: 10px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary { background: #f0fdf4; padding: 20px; border-radius: 8px; margin-top: 30px; }
          .summary h3 { color: #006847; margin-top: 0; }
          .summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
          .summary-item { background: white; padding: 15px; border-radius: 6px; }
          .summary-item strong { display: block; font-size: 24px; color: #006847; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="logo-header">
          ${logoSVG}
          <div>
            <h1>Sistema de Gestion de Quirofanos</h1>
            <p>Centinela Salud</p>
          </div>
        </div>

        <div class="header">
          <h2>${content.title}</h2>
          <p class="periodo">Periodo: ${content.periodo}</p>
          <p class="periodo">Generado: ${new Date().toLocaleString('es-MX')}</p>
        </div>

        <table>
          <thead>
            <tr>${content.headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${content.rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>

        <div class="summary">
          <h3>Resumen</h3>
          <div class="summary-grid">
            ${Object.entries(content.summary).map(([key, value]) => `
              <div class="summary-item">
                <strong>${value}</strong>
                <span>${key.replace(/([A-Z])/g, ' $1').trim()}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="footer">
          <p><strong>Centinela Salud</strong></p>
          <p>Documento generado automaticamente por el Sistema de Gestion de Quirofanos</p>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  const handleGenerateReport = async (format) => {
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Generar y descargar el archivo
      if (format === 'excel' || format === 'csv') {
        downloadCSV(selectedReport)
      } else if (format === 'pdf') {
        downloadPDF(selectedReport)
      }

      // Agregar al historial
      const reportData = allReportTypes.find(r => r.id === selectedReport)
      const newReport = {
        id: Date.now(),
        tipo: selectedReport,
        nombre: reportData.name,
        fecha: new Date().toISOString().split('T')[0],
        periodo: `${new Date(dateRange.start).toLocaleDateString('es-MX', { month: 'short' })} - ${new Date(dateRange.end).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })}`,
        formato: format.toUpperCase(),
        tamanio: format === 'pdf' ? '1.2 MB' : '0.5 MB'
      }
      setReportHistory([newReport, ...reportHistory])

      toast.success(`Reporte ${format.toUpperCase()} generado exitosamente`)
      setShowPreview(false)
    } catch (error) {
      toast.error('Error al generar el reporte')
    } finally {
      setLoading(false)
    }
  }

  // Descargar reporte del historial
  const handleDownloadFromHistory = (report) => {
    // Simular descarga basada en el historial
    const content = generateReportContent(report.tipo)
    if (report.formato === 'PDF') {
      downloadPDF(report.tipo)
    } else {
      downloadCSV(report.tipo)
    }
    toast.success(`Descargando ${report.nombre}...`)
  }

  const getReportIcon = (tipo) => {
    const report = allReportTypes.find(r => r.id === tipo)
    return report?.icon || FileText
  }

  const getReportColor = (tipo) => {
    const report = allReportTypes.find(r => r.id === tipo)
    return report?.color || 'gray'
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">Reportes</h1>
            {isOperador && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500">
            {isOperador
              ? 'Genera reportes de tu unidad medica'
              : 'Genera y descarga reportes del sistema'
            }
          </p>
        </div>

        {/* Info de contexto */}
        {isAdmin && (
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-green-50 border border-green-200">
            <Shield className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-xs text-green-600 font-medium">Modo Administrador</p>
              <p className="text-sm font-semibold text-green-800">Todos los hospitales</p>
            </div>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Reportes Generados"
          value={stats.reportesGenerados}
          subtitle="Total historico"
          icon={FileText}
          trend="up"
          trendValue="+12%"
          color="emerald"
        />
        <KPICard
          title="Este Mes"
          value={stats.estesMes}
          subtitle="Reportes generados"
          icon={Calendar}
          trend="up"
          trendValue="+3"
          color="blue"
        />
        <KPICard
          title="Descargas"
          value={stats.descargas}
          subtitle="Total de descargas"
          icon={Download}
          trend="up"
          trendValue="+25%"
          color="purple"
        />
        <KPICard
          title="Mas Usado"
          value={0}
          subtitle={stats.tipoMasUsado}
          icon={BarChart3}
          color="teal"
          showDashOnZero={true}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Report Types */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Tipo de Reporte</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {reportTypes.map((report) => {
              const IconComponent = report.icon
              return (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all group ${
                    selectedReport === report.id
                      ? `border-${report.color}-500 bg-${report.color}-50 shadow-md`
                      : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${
                      selectedReport === report.id
                        ? `bg-${report.color}-100`
                        : 'bg-gray-100 group-hover:bg-gray-200'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${
                        selectedReport === report.id
                          ? `text-${report.color}-600`
                          : 'text-gray-400'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold mb-1 transition-colors ${
                        selectedReport === report.id ? 'text-gray-800' : 'text-gray-700'
                      }`}>
                        {report.name}
                      </h3>
                      <p className="text-sm text-gray-500">{report.description}</p>
                    </div>
                    {selectedReport === report.id && (
                      <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Configuracion</h2>

          <div className="space-y-4">
            {/* Quick range buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rango rapido
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setQuickRange('week')}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Ultima semana
                </button>
                <button
                  onClick={() => setQuickRange('month')}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Ultimo mes
                </button>
                <button
                  onClick={() => setQuickRange('quarter')}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Ultimo trimestre
                </button>
                <button
                  onClick={() => setQuickRange('year')}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Ultimo ano
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Inicio
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Fin
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <button
              onClick={handlePreview}
              disabled={loading || !selectedReport || !dateRange.start || !dateRange.end}
              className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Generando...
                </>
              ) : (
                <>
                  <Eye size={20} />
                  Vista Previa
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Historial de reportes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Historial de Reportes</h2>
            <p className="text-sm text-gray-500">{filteredHistory.length} reportes generados</p>
          </div>
          <select
            value={filterHistoryType}
            onChange={(e) => setFilterHistoryType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">Todos los tipos</option>
            {allReportTypes.filter(r => !r.adminOnly || isAdmin).map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredHistory.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay reportes en el historial</p>
            </div>
          ) : (
            filteredHistory.map((report) => {
              const IconComponent = getReportIcon(report.tipo)
              const color = getReportColor(report.tipo)
              return (
                <div key={report.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-${color}-100`}>
                      <IconComponent className={`w-5 h-5 text-${color}-600`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-800">{report.nombre}</h3>
                      <p className="text-sm text-gray-500">Periodo: {report.periodo}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-sm text-gray-800">{report.fecha}</p>
                      <p className="text-xs text-gray-500">{report.formato} - {report.tamanio}</p>
                    </div>
                    <button
                      onClick={() => handleDownloadFromHistory(report)}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Download className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <ReportPreviewModal
          report={selectedReport}
          dateRange={dateRange}
          onClose={() => setShowPreview(false)}
          onGenerate={handleGenerateReport}
        />
      )}
    </div>
  )
}
